import { AccountType, Customize, InstallationDetailsState, PdfContent, PdfDataInput, PdfEntry } from 'graphql/types'
import { TFunction } from 'i18next'
import { BankDetailsState } from 'store/BankDetails/BankDetails.reducer'
import { ContactDataState } from 'store/ContactData/ContactData.reducer'
import { GeneralState } from 'store/GeneralState/GeneralState.reducer'
import { PortabilityState } from 'store/PortabilityState/PortabilityState.reducer'
import { AppState } from 'store/store'
import { generateVZFDataForText } from './generateVZFDataForText'
import { salutationIsCompany } from './testable/functions'

export const stateToPDFdata = (
    appState: AppState,
    b2b: boolean,
    t: TFunction,
    customizeJsData: Customize | undefined,
): PdfDataInput => {
    const vzfData = generateVZFDataForText(appState.generalState.vzfID, appState, t, b2b, false, customizeJsData)
    const pdfEntries: PdfEntry[] = []
    const pdfCompleteEntries: PdfEntry[] = []

    pdfCompleteEntries.push(
        getContactDataCompletePdf(appState.contactData, appState.availabilityCheck, t, b2b, customizeJsData),
    )
    if (appState.contactData.deviatingDeliveryAddress || appState.contactData.deviatingDeliveryDhl) {
        pdfCompleteEntries.push(getDeliveryAddressCompletePdf(appState.contactData, t, customizeJsData))
    }
    if (appState.contactData.deviatingBillingAddress) {
        pdfCompleteEntries.push(getBillingAddressCompletePdf(appState.contactData, t, customizeJsData))
    }

    if (customizeJsData && customizeJsData.installationDetailsConfiguration.enablePage) {
        const installationData = getInstallationDataPdf(appState.generalState.installationDetails, t, customizeJsData)
        pdfEntries.push(installationData)
        pdfCompleteEntries.push(installationData)
    }

    if (customizeJsData && customizeJsData.telephoneOptions.enablePage) {
        const telephoneData = getTelephoneContractDataPdf(appState.generalState, appState.portabilityState, false, t)
        const telephoneDataComplete = getTelephoneContractDataPdf(
            appState.generalState,
            appState.portabilityState,
            true,
            t,
        )
        pdfEntries.push(telephoneData)
        pdfCompleteEntries.push(telephoneDataComplete)
    }

    const bankData = getBankDataPdf(appState.generalState, appState.bankDetails, t, customizeJsData, b2b)
    pdfEntries.push(bankData)
    pdfCompleteEntries.push(
        getBankDataCompletePdf(appState.generalState, appState.bankDetails, t, customizeJsData, b2b),
    )

    if (customizeJsData && customizeJsData.installationConfiguration.enablePage) {
        const desiredDateData = getDesiredDataPdf(appState.generalState, t)
        pdfEntries.push(desiredDateData)
        pdfCompleteEntries.push(desiredDateData)
    }

    return {
        PDFSummaryEntries: pdfEntries,
        PDFSummaryCompleteEntries: pdfCompleteEntries,
        costs: vzfData.costs,
    }
}

const getInstallationDataPdf = (
    installationData: InstallationDetailsState,
    t: TFunction,
    customizeJsData: Customize | undefined,
): PdfEntry => {
    const content: PdfContent[] = []

    if (customizeJsData) {
        if (customizeJsData.installationDetailsConfiguration.junctionBox.identifier !== '') {
            content.push({
                Key: parseHTML(t('orderOverviewStrings.installation.junctionBox')),
                Value: parseHTML(t('installationDetailsStrings.junctionBox.' + installationData.junctionBox)),
                Description: [],
            })
        }

        if (customizeJsData.installationDetailsConfiguration.houseConnection) {
            content.push({
                Key: parseHTML(t('orderOverviewStrings.installation.houseConnection')),
                Value: parseHTML(t('installationDetailsStrings.houseConnection.' + installationData.houseConnection)),
                Description: [],
            })
            if (installationData.houseConnection === 'OptionTitle1') {
                content.push({
                    Key: parseHTML(t('installationDetailsStrings.houseConnectionInputLabel')),
                    Value: parseHTML(installationData.houseConnectionInput),
                    Description: [],
                })
            }
        }
        if (customizeJsData.installationDetailsConfiguration.opticFiber) {
            content.push({
                Key: parseHTML(t('orderOverviewStrings.installation.opticFiber')),
                Value: parseHTML(t('installationDetailsStrings.opticFiber.' + installationData.opticFiber)),
                Description: [],
            })
            if (installationData.houseConnection === 'OptionTitle1') {
                content.push({
                    Key: parseHTML(t('installationDetailsStrings.opticFiberInputLabel')),
                    Value: parseHTML(installationData.opticFiberInput),
                    Description: [],
                })
            }
        }
    }

    return { Headline: t('orderOverviewStrings.installation.categoryTitle'), Content: content }
}

const getTelephoneContractDataPdf = (
    generalState: GeneralState,
    portabilityState: PortabilityState,
    displayComplete: boolean,
    t: TFunction,
): PdfEntry => {
    const content: PdfContent[] = []

    if (generalState.customizeJsData) {
        if (generalState.customizeJsData.telephoneOptions.enablePage) {
            const portabilityValue = generalState.configuration.get(
                generalState.customizeJsData.portabilityConfiguration.showPortability.identifier,
            )
            if (portabilityValue === generalState.customizeJsData.portabilityConfiguration.showPortability.wishValue) {
                content.push({
                    Key: t('orderOverviewStrings.telephoneContractData.portability.porting'),
                    Value:
                        portabilityState.phoneOptions[0].areaCode === '' &&
                        portabilityState.phoneOptions[0].number === ''
                            ? t('orderOverviewStrings.telephoneContractData.portability.no')
                            : t('orderOverviewStrings.telephoneContractData.portability.yes'),
                    Description: [],
                })
                content.push({
                    Key: t(
                        'orderOverviewStrings.telephoneContractData.' +
                            generalState.customizeJsData.portabilityConfiguration.showPortability.identifier,
                    ),
                    Value: portabilityState.phoneOptions[0].areaCode + portabilityState.phoneOptions[0].number,
                    Description: [],
                })
                if (displayComplete) {
                    if (portabilityState.selectedRadios.includes('contractHolderNo')) {
                        content.push({
                            Key: t(
                                'orderOverviewStrings.telephoneContractData.portability.differentPreviousContractHolder',
                            ),
                            Value: t('orderOverviewStrings.telephoneContractData.yes'),
                            Description: [],
                        })
                        portabilityState.contractHolderOptions.forEach((contractHolder, index) => {
                            content.push({
                                Key:
                                    t(
                                        'orderOverviewStrings.telephoneContractData.portability.previousContractHolderName',
                                    ) + (portabilityState.contractHolderOptions.length > 1 ? ' ' + (index + 1) : ''),
                                Value: contractHolder.firstName + ' ' + contractHolder.lastName,
                                Description: [],
                            })
                        })
                    }
                    if (portabilityState.selectedRadios.includes('addressNo')) {
                        if (
                            generalState.customizeJsData &&
                            generalState.customizeJsData.portabilityConfiguration.previousContractAddressHolder
                        ) {
                            content.push({
                                Key: t(
                                    'orderOverviewStrings.telephoneContractData.portability.differentConnectionAddress',
                                ),
                                Value: `${t(portabilityState.addressHolder.salutation)}${
                                    portabilityState.addressHolder.salutation === 'Firma'
                                        ? ' ' +
                                          portabilityState.addressHolder.company +
                                          (portabilityState.addressHolder.companyLegalForm
                                              ? ' ' + portabilityState.addressHolder.companyLegalForm
                                              : '') +
                                          ','
                                        : ''
                                } ${portabilityState.addressHolder.name} ${
                                    portabilityState.addressHolder.lastName
                                }`.trim(),
                                Description: [],
                            })
                        }
                        content.push({
                            Key:
                                generalState.customizeJsData &&
                                generalState.customizeJsData.portabilityConfiguration.previousContractAddressHolder
                                    ? ''
                                    : t(
                                          'orderOverviewStrings.telephoneContractData.portability.differentConnectionAddress',
                                      ),
                            Value: `${portabilityState.address.street} ${portabilityState.address.houseNumber} ${
                                portabilityState.address.zip
                            } ${portabilityState.address.city}${
                                portabilityState.address.district !== ''
                                    ? ' (' + portabilityState.address.district + ')'
                                    : ''
                            }`.trim(),
                            Description: [],
                        })
                    }
                }

                content.push({
                    Key: t('orderOverviewStrings.telephoneContractData.portability.provider'),
                    Value: portabilityState.selectedProvider,
                    Description: [],
                })
                if (portabilityState.selectedRadios.includes('terminatedContractNo')) {
                    content.push({
                        Key: t('orderOverviewStrings.telephoneContractData.portability.terminatedContract'),
                        Value: portabilityState.endOfContract,
                        Description: [],
                    })
                } else {
                    content.push({
                        Key: t('orderOverviewStrings.telephoneContractData.portability.terminatedContract'),
                        Value: t('orderOverviewStrings.telephoneContractData.no'),
                        Description: [],
                    })
                }
            }
        }
        generalState.configuration.forEach((value, key) => {
            if (generalState.customizeJsData) {
                let showEntry = false
                for (const configuration of generalState.customizeJsData.telephoneOptions.telephoneConfiguration) {
                    if (configuration.type === 'CONDITIONAL' && configuration.render) {
                        const conditionValue = generalState.configuration.get(configuration.condition?.identifier ?? '')
                        if (conditionValue !== configuration.condition?.value) continue
                        for (const subConfiguration of configuration.render) {
                            if (subConfiguration.identifier === key) {
                                showEntry = true
                            }
                        }
                        if (showEntry) break
                    } else if (configuration.identifier === key) {
                        showEntry = true
                        break
                    }
                }
                if (
                    showEntry &&
                    key !== 'invoiceSend' &&
                    key !== 'informationServices' &&
                    key !== generalState.customizeJsData.portabilityConfiguration.showPortability.identifier &&
                    generalState.customizeJsData.orderOverview.legalTerms.checkboxes.some(
                        (checkbox) => checkbox.value === key,
                    ) === false &&
                    ((generalState.customizeJsData.orderOverview.newsletters &&
                        generalState.customizeJsData.orderOverview.newsletters.checkboxes.includes(key) === false) ||
                        generalState.customizeJsData.orderOverview.newsletters === undefined) &&
                    ((generalState.customizeJsData.orderOverview.vzf &&
                        generalState.customizeJsData.orderOverview.vzf.checkboxes.includes(key) === false) ||
                        generalState.customizeJsData.orderOverview.vzf === undefined)
                ) {
                    content.push({
                        Key: t('orderOverviewStrings.telephoneContractData.' + key),
                        Value:
                            typeof value === 'string'
                                ? t('orderOverviewStrings.telephoneContractData.' + value)
                                : value
                                      .map((value) => t('orderOverviewStrings.telephoneContractData.' + value))
                                      .join(', '),
                        Description: [],
                    })
                }
            }
        })
        if (generalState.configuration.get('entryInPhoneBook')) {
            const informationServicesConfig = generalState.configuration.get('informationServices') as string[]
            const inverseSearchKey =
                informationServicesConfig && informationServicesConfig.includes('inverseSearch') ? 'yes' : 'no'

            content.push({
                Key: t('orderOverviewStrings.telephoneContractData.' + 'inverseSearchTitle'),
                Value: t('orderOverviewStrings.telephoneContractData.' + inverseSearchKey),
                Description: [],
            })
        }
    }

    return { Headline: parseHTML(t('orderOverviewStrings.telephoneContractData.categoryTitle')), Content: content }
}

const getBankDataPdf = (
    generalState: GeneralState,
    bankData: BankDetailsState,
    t: TFunction,
    customize: Customize | undefined,
    b2b: boolean,
): PdfEntry => {
    const content: PdfContent[] = []

    if (bankData.accountType === AccountType.IBAN) {
        content.push({
            Key: t('orderOverviewStrings.bankDetails.accountType'),
            Value: t('orderOverviewStrings.bankDetails.selfpay'),
            Description: [],
        })

        let iban = bankData.iban.replace(/ /g, '').trim()
        iban = iban.substring(0, 3) + 'XXXXXXXXXXXXXXXX' + iban.substring(iban.length - 3, iban.length)
        content.push({
            Key: t('orderOverviewStrings.bankDetails.iban'),
            Value: iban,
            Description: [],
        })

        if (customize && customize.bankDetails.displayBIC) {
            let bic = bankData.bic.replace(/ /g, '').trim()
            bic = bic.substring(0, 3) + 'XXXXXX' + bic.substring(bic.length - 2, bic.length)
            content.push({
                Key: t('orderOverviewStrings.bankDetails.bic'),
                Value: bic,
                Description: [],
            })
        }

        if (customize && customize.bankDetails.displayDayOfTransfer) {
            content.push({
                Key: t('bankDetailsStrings.transferMonthlyTitle'),
                Value:
                    bankData.dayOfTransfer === '15'
                        ? t('bankDetailsStrings.transfer15')
                        : t('bankDetailsStrings.transfer29'),
                Description: [],
            })
        }
    } else {
        content.push({
            Key: t('orderOverviewStrings.bankDetails.accountType'),
            Value: t('orderOverviewStrings.bankDetails.transfer'),
            Description: [],
        })
    }
    if (generalState.configuration.get('invoiceSend')) {
        const invoiceSendValue = generalState.configuration.get('invoiceSend') as string

        content.push({
            Key: t('orderOverviewStrings.telephoneContractData.' + 'invoiceSend'),
            Value: t(
                'orderOverviewStrings.telephoneContractData.' + b2b ? invoiceSendValue + '_B2B' : invoiceSendValue,
            ),
            Description: [],
        })
    }

    return { Headline: t('orderOverviewStrings.bankDetails.categoryTitle'), Content: content }
}

const getDesiredDataPdf = (generalState: GeneralState, t: TFunction): PdfEntry => {
    const content: PdfContent[] = []

    if (generalState.earliestDatePossible || generalState.desiredDate === null) {
        content.push({
            Key: t('orderOverviewStrings.desiredDate.connection'),
            Value: t('orderOverviewStrings.desiredDate.earliestDatePossible'),
            Description: [],
        })
    } else {
        content.push({
            Key: t('orderOverviewStrings.desiredDate.connection'),
            Value: generalState.desiredDate.toLocaleDateString(),
            Description: [],
        })
    }

    return { Headline: t('orderOverviewStrings.desiredDate.categoryTitle'), Content: content }
}

const getContactDataCompletePdf = (
    contactData: ContactDataState,
    address: AppState['availabilityCheck'],
    t: TFunction,
    b2b: boolean,
    customizeJsData: Customize | undefined,
): PdfEntry => {
    const content: PdfContent[] = []

    content.push({
        Key: t('orderOverviewStrings.contactData.personalSalutation'),
        Value: t(contactData.personalSalutation),
        Description: [],
    })
    if (contactData.personalTitle.trim() !== '') {
        content.push({
            Key: t('orderOverviewStrings.contactData.personalTitle'),
            Value: contactData.personalTitle,
            Description: [],
        })
    }
    if (contactData.existingCustomerRadio === 'yes') {
        content.push({
            Key: t('orderOverviewStrings.contactData.existingCustomerNumber'),
            Value: contactData.existingCustomerNumber,
            Description: [],
        })
    }
    if (b2b) {
        content.push({
            Key: t('contactDataStrings.companyName'),
            Value: contactData.company,
            Description: [],
        })
        if (customizeJsData?.contactDataViewConfiguration) {
            if (
                customizeJsData.contactDataViewConfiguration.enableLegalForm ||
                customizeJsData.contactDataViewConfiguration.enableLegalFormB2C
            ) {
                content.push({
                    Key: t('contactDataStrings.companyLegalForm'),
                    Value: contactData.companyLegalForm,
                    Description: [],
                })
            }
            if (customizeJsData.contactDataViewConfiguration.enableCompanyRegisterEntry) {
                content.push({
                    Key: t('contactDataStrings.companyRegisterEntry'),
                    Value: contactData.companyRegisterEntry,
                    Description: [],
                })
            }
            if (customizeJsData.contactDataViewConfiguration.enableCompanyLocation) {
                content.push({
                    Key: t('contactDataStrings.companyLocation'),
                    Value: contactData.companyLocation,
                    Description: [],
                })
            }
            if (customizeJsData.contactDataViewConfiguration.enableCompanyTaxID) {
                content.push({
                    Key: t('contactDataStrings.companyTaxId'),
                    Value: contactData.companyId,
                    Description: [],
                })
            }
        }
    } else if (salutationIsCompany(contactData.personalSalutation)) {
        content.push({
            Key: t('contactDataStrings.companyName'),
            Value: contactData.company,
            Description: [],
        })
        if (customizeJsData?.contactDataViewConfiguration?.enableAlternativeLegalForm) {
            content.push({
                Key: t('contactDataStrings.companyLegalForm'),
                Value: contactData.companyLegalForm,
                Description: [],
            })
        }
    }

    content.push(
        {
            Key: t('orderOverviewStrings.contactData.personalFullName'),
            Value: contactData.personalName + ' ' + contactData.personalLastName,
            Description: [],
        },
        {
            Key: t('orderOverviewStrings.contactData.personalAddress'),
            Value: `${address.selectedStreet} ${address.selectedHouseNumber} ${address.selectedAddition}`.trim(),
            Description: [],
        },
        {
            Key: '',
            Value: ` ${address.zip} ${address.selectedCity}${
                address.selectedDistrict ? ' (' + address.selectedDistrict + ')' : ''
            } `.trim(),
            Description: [],
        },
        {
            Key: '',
            Value: contactData.personalAdditionalAddressInfo || '',
            Description: [],
        },
        {
            Key: t('orderOverviewStrings.contactData.personalEmail'),
            Value: contactData.personalEmail,
            Description: [],
        },
        {
            Key: t('orderOverviewStrings.contactData.personalTelephone'),
            Value: contactData.personalTelephone,
            Description: [],
        },
        {
            Key: t('orderOverviewStrings.contactData.personalMobilePhone'),
            Value: contactData.personalMobilePhone,
            Description: [],
        },
        {
            Key: t('orderOverviewStrings.contactData.personalBirthDate'),
            Value: contactData.personalBirthDate,
            Description: [],
        },
    )

    return { Headline: t('orderOverviewStrings.contactData.categoryTitle'), Content: content }
}

const getDeliveryAddressCompletePdf = (
    contactData: ContactDataState,
    t: TFunction,
    customizeJsData: Customize | undefined,
): PdfEntry => {
    const content: PdfContent[] = []

    content.push({
        Key: t('orderOverviewStrings.contactData.personalSalutation'),
        Value: t(contactData.deliverySalutation),
        Description: [],
    })
    if (contactData.deliveryCompany.trim().length > 0) {
        content.push({
            Key: t('contactDataStrings.companyName'),
            Value: contactData.deliveryCompany,
            Description: [],
        })
        if (customizeJsData?.contactDataViewConfiguration?.enableAlternativeLegalForm) {
            content.push({
                Key: t('contactDataStrings.companyLegalForm'),
                Value: contactData.deliveryCompanyLegalForm,
                Description: [],
            })
        }
    }
    if (contactData.deliveryTitle.trim() !== '') {
        content.push({
            Key: t('orderOverviewStrings.contactData.personalTitle'),
            Value: contactData.deliveryTitle,
            Description: [],
        })
    }
    content.push({
        Key: t('orderOverviewStrings.contactData.personalFullName'),
        Value: contactData.deliveryName + ' ' + contactData.deliveryLastName,
        Description: [],
    })
    if (contactData.deviatingDeliveryDhl) {
        content.push({
            Key: t('orderOverviewStrings.contactData.personalAddress'),
            Value: `Packstation ${contactData.deliveryHouseNumber}`,
            Description: [],
        })
        content.push({
            Key: '',
            Value: `${contactData.deliveryZip} ${contactData.deliveryCity}`.trim(),
            Description: [],
        })
        if (contactData.deliveryCountryAddr) {
            content.push({
                Key: '',
                Value: contactData.deliveryCountryAddr,
                Description: [],
            })
        }
        if (contactData.deliveryAdditionalAddrInfo) {
            content.push({
                Key: '',
                Value: 'Ihre Postnummer ' + contactData.deliveryAdditionalAddrInfo,
                Description: [],
            })
        }
    } else {
        content.push({
            Key: t('orderOverviewStrings.contactData.personalAddress'),
            Value: `${contactData.deliveryStreet} ${contactData.deliveryHouseNumber}${
                contactData.deliveryAdditionalAddrInfo ? ' ' + contactData.deliveryAdditionalAddrInfo : ''
            }`.trim(),
            Description: [],
        })
        content.push({
            Key: '',
            Value: `${contactData.deliveryZip} ${contactData.deliveryCity}${
                contactData.deliveryDistrict ? ' (' + contactData.deliveryDistrict + ')' : ''
            }`.trim(),
            Description: [],
        })
        if (contactData.deliveryCountryAddr) {
            content.push({
                Key: '',
                Value: contactData.deliveryCountryAddr,
                Description: [],
            })
        }
    }

    return {
        Headline: t('orderOverviewStrings.contactData.deliveryAddress'),
        Content: content,
    }
}

const getBillingAddressCompletePdf = (
    contactData: ContactDataState,
    t: TFunction,
    customizeJsData: Customize | undefined,
): PdfEntry => {
    const content: PdfContent[] = []

    content.push({
        Key: t('orderOverviewStrings.contactData.personalSalutation'),
        Value: t(contactData.billingSalutation),
        Description: [],
    })
    if (contactData.billingCompany.trim().length > 0) {
        content.push({
            Key: t('contactDataStrings.companyName'),
            Value: contactData.billingCompany,
            Description: [],
        })
        if (customizeJsData?.contactDataViewConfiguration?.enableAlternativeLegalForm) {
            content.push({
                Key: t('contactDataStrings.companyLegalForm'),
                Value: contactData.billingCompanyLegalForm,
                Description: [],
            })
        }
    }
    if (contactData.billingTitle.trim() !== '') {
        content.push({
            Key: t('orderOverviewStrings.contactData.personalTitle'),
            Value: contactData.billingTitle,
            Description: [],
        })
    }
    content.push(
        {
            Key: t('orderOverviewStrings.contactData.personalFullName'),
            Value: contactData.billingName + ' ' + contactData.billingLastName,
            Description: [],
        },
        {
            Key: t('orderOverviewStrings.contactData.personalAddress'),
            Value: `${contactData.billingStreet} ${contactData.billingHouseNumber}${
                contactData.billingAdditionalAddrInfo ? ' ' + contactData.billingAdditionalAddrInfo : ''
            }`.trim(),
            Description: [],
        },
        {
            Key: '',
            Value: `${contactData.billingZip} ${contactData.billingCity}${
                contactData.billingDistrict ? ' (' + contactData.billingDistrict + ')' : ''
            }`.trim(),
            Description: [],
        },
    )
    if (contactData.billingCountryAddr) {
        content.push({
            Key: '',
            Value: contactData.billingCountryAddr,
            Description: [],
        })
    }

    return {
        Headline: t('orderOverviewStrings.contactData.billingAddress'),
        Content: content,
    }
}

const getBankDataCompletePdf = (
    generalState: GeneralState,
    bankData: BankDetailsState,
    t: TFunction,
    customize: Customize | undefined,
    b2b: boolean,
): PdfEntry => {
    const content: PdfContent[] = []

    if (bankData.accountType === AccountType.IBAN) {
        content.push({
            Key: t('orderOverviewStrings.bankDetails.accountType'),
            Value: t('orderOverviewStrings.bankDetails.selfpay'),
            Description: [],
        })
        content.push({
            Key: t('orderOverviewStrings.bankDetails.iban'),
            Value: bankData.iban,
            Description: [],
        })
        if (bankData.bic) {
            content.push({
                Key: t('orderOverviewStrings.bankDetails.bic'),
                Value: bankData.bic,
                Description: [],
            })
        }
        if (bankData.bankName) {
            content.push({
                Key: t('orderOverviewStrings.bankDetails.bankName'),
                Value: bankData.bankName,
                Description: [],
            })
        }
        if (customize?.bankDetails?.displayDayOfTransfer) {
            content.push({
                Key: t('bankDetailsStrings.transferMonthlyTitle'),
                Value:
                    bankData.dayOfTransfer === '15'
                        ? t('bankDetailsStrings.transfer15')
                        : t('bankDetailsStrings.transfer29'),
                Description: [],
            })
        }
    } else {
        content.push({
            Key: t('orderOverviewStrings.bankDetails.accountType'),
            Value: t('orderOverviewStrings.bankDetails.transfer'),
            Description: [],
        })
    }
    if (generalState.configuration.get('invoiceSend')) {
        const invoiceSendValue = generalState.configuration.get('invoiceSend') as string
        content.push({
            Key: t('orderOverviewStrings.telephoneContractData.invoiceSend'),
            Value: t(
                'orderOverviewStrings.telephoneContractData.' + (b2b ? invoiceSendValue + '_B2B' : invoiceSendValue),
            ),
            Description: [],
        })
    }
    if (bankData.differentAccountHolder) {
        const ah = bankData.accountHolderData
        content.push({
            Key: t('orderOverviewStrings.bankDetails.accountHolder.salutation'),
            Value: t(ah.salutation),
            Description: [],
        })
        if (salutationIsCompany(ah.salutation)) {
            content.push({
                Key: t('contactDataStrings.companyName'),
                Value: ah.company,
                Description: [],
            })
            if (customize?.contactDataViewConfiguration?.enableAlternativeLegalForm) {
                content.push({
                    Key: t('contactDataStrings.companyLegalForm'),
                    Value: ah.companyLegalForm,
                    Description: [],
                })
            }
        }
        content.push(
            {
                Key: t('orderOverviewStrings.bankDetails.accountHolder.name'),
                Value: ah.name + ' ' + ah.lastName,
                Description: [],
            },
            {
                Key: t('orderOverviewStrings.bankDetails.accountHolder.address'),
                Value: `${ah.street} ${ah.houseNumber}${
                    ah.additionalInfoAddress ? ' ' + ah.additionalInfoAddress : ''
                }`.trim(),
                Description: [],
            },
            {
                Key: '',
                Value: `${ah.zip} ${ah.city}${ah.district ? ' (' + ah.district + ')' : ''}`.trim(),
                Description: [],
            },
        )
        if (ah.countryAddress) {
            content.push({
                Key: '',
                Value: ah.countryAddress,
                Description: [],
            })
        }
    }

    return { Headline: t('orderOverviewStrings.bankDetails.categoryTitle'), Content: content }
}

const parseHTML = (html: string): string => {
    const div = document.createElement('div')
    div.innerHTML = html.replace(/&#173;/g, '')
    return div.innerText
}

export const getMultiplicator = (n: number | undefined): number => {
    let multiplicator = 1
    if (n && n > 1) {
        multiplicator = n
    }
    return multiplicator
}
